import {Location} from './location';

export interface Flow {
  id: number;
  name: string;
  isActive?: boolean;
  isNative?: boolean;
  serviceId?: number;
  events?: EventType[]
}

export interface FlowCreate {
  "name": string,
  "isActive": boolean,
  "isNative": boolean,
  "serviceId"?: number
}

export interface FlowService {
  "id": number,
  "flowId": number,
  "serviceId": number,
}

export interface EventType {
  id: number;
  flowId?: number;
  name: string;
  eventClassId?: number;
  eventClass?: EventClass;
  isActive?: boolean;
  sequence: number;
  locationId?: number;
  eventTypeParentId?: number;
  events?: Event[];
  location?: Location
  eventTriggers?: EventTrigger[];
  mojitoDynamicField?: string;

  // Variables de front
  activator?: Activator
  icon?: string;
  color?: string;
}

export interface EventTypeCreate {
  name?: string,
  isActive?: boolean,
  sequence?: number,
  flowId?: number,
  eventClassId?: number,
  color?: string,
  icon?: string,
  mojitoDynamicField?: string;
}

export interface EventTriggerCreate {
  name?: string;
  integrationField?: string;
  eventTypeId?: number;
  documentUploaded?: boolean;
  documentValidation?: boolean;
  expirationDate?: string;
  eventTriggerTypeId?: number;
}

export interface EventTrigger {
  id: number;
  name?: string;
  integrationField?: string;
  eventTypeId?: number;
  documentUploaded?: boolean;
  documentValidation?: boolean;
  expirationDate?: string;
  eventTriggerTypeId?: number;
  eventTriggerType: {id: number, name: string};
  documentTypeId?: number;
  documentType?: {id: number, name: string}
}

export interface EventTriggerType{
  id: number;
  name: string;
}

export interface EventClass {
  id: number;
  name: string;
  isActive: boolean;
}

export interface Event {
  id: number;
  eventTypeId: number;
  serviceOrderId: number;
  locationId: number;
  dateTime: string;
  isActive: boolean;
  integrationField: string,
  integrationValue: string,
}

export interface EventCreate {
  "eventTypeId": number,
  "serviceOrderId": number,
  "locationId"?: number,
  "dateTime": string,
  "isActive": boolean,
}


export interface Activator {
  name: string;
  value: any; // Cualquier tipo de documento, cualquier estado de Mojito 360 o fecha manual
  location?: Location;
  mojitoDynamicField?: {enum: string, label: string};
}


/**
 * Diff de una edicion de flujo, listo para mandar al backend.
 * Se construye con FlowEventService.buildFlowDiff() y se usa tanto para el
 * preview de impacto como para el commit transaccional.
 */
export interface FlowDiff {
  /** Etapas nuevas. Llevan el id negativo temporal que asigna el front. */
  added: EventType[];
  /** Etapas existentes con cambios en nombre, icono, color, activador o secuencia. */
  updated: EventType[];
  /** Ids de etapas que se retiran del flujo. El backend hace soft-delete, nunca DELETE. */
  hidden: number[];
  /** Orden final completo de las etapas activas. */
  order: {id: number, sequence: number}[];
}

/** Motivo por el que una SO vuelve a 'Orden creada'. */
export type FlowEditResetReason = 'ReorderCrossesState' | 'HistoryNotPrefix';

/** Motivo por el que una SO cambia de estado sin llegar a resetearse. */
export type FlowEditReprojectReason = 'StageHidden' | 'NewStageAhead';

/** Motivo por el que una SO quedaria sin forma de avanzar tras la edicion. */
export type FlowEditRiskReason =
  'NoIntegrationBeforeAutomaticStage'
  | 'MojitoFieldWillNeverArrive'
  | 'DocumentAlreadySettledBeforeStageExisted';

export interface FlowEditAffectedOrder {
  id: number;
  reference: string;
  currentStateId?: number;
  currentStateName?: string;
  mojitoIntegrationStatus?: string;
  reason: FlowEditResetReason;
}

export interface FlowEditReprojectedOrder {
  id: number;
  reference: string;
  fromStateId: number;
  toStateId: number;
  toStateName: string;
  reason: FlowEditReprojectReason;
}

export interface FlowEditRiskOrder {
  id: number;
  reference: string;
  blockingStageId: number;
  blockingStageName: string;
  reason: FlowEditRiskReason;
}

export interface FlowEditHiddenStage {
  eventTypeId: number;
  name: string;
  ordersWithHistory: number;
}

/** Impacto calculado por el backend en modo dry-run, antes de confirmar la edicion. */
export interface FlowEditPreview {
  /** Version del flujo, viaja como If-Match en el commit para detectar ediciones concurrentes. */
  flowVersion: string;
  /** Ata el commit a este preview. Si el flujo cambia mientras tanto, el backend responde 409. */
  confirmToken: string;
  totalOrders: number;
  ordersUnchanged: number;
  // La API omite las colecciones vacias, asi que estas listas pueden no venir. El servicio
  // las repone al recibir la respuesta; se declaran opcionales para que nadie de por hecho
  // que estan ahi si algun dia se consume la respuesta en crudo.
  ordersReset?: FlowEditAffectedOrder[];
  ordersReprojected?: FlowEditReprojectedOrder[];
  ordersUntouched?: {created: number, finalized: number};
  ordersAtRisk?: FlowEditRiskOrder[];
  hiddenStagesInUse?: FlowEditHiddenStage[];
  warnings?: string[];
}

export interface FlowEditResult {
  flow: Flow;
  flowVersion: string;
  ordersReset: number;
  ordersReprojected: number;
  eventsDeactivated: number;
  hiddenStages: number[];
}

/**
 * Respuesta del commit. Por encima del umbral de ordenes que tenga configurado el
 * backend, la edicion se aplica en segundo plano y llega `jobId` en vez del resultado:
 * hay que seguirla con getFlowEditJob() hasta que termine.
 */
export interface FlowEditCommit {
  result?: FlowEditResult;
  jobId?: string;
}

/** Etapa citada en el historial de ediciones. Las ocultadas siguen resolviendo su nombre. */
export interface FlowEditHistoryStage {
  id: number;
  name: string;
}

/**
 * Una edicion confirmada del flujo (FlowEditAudit), tal como la devuelve
 * GET /api/flow/{flowId}/edit-history. Contrato propuesto en SOU-567.
 */
export interface FlowEditHistoryEntry {
  id: number;
  createdOn: string;
  /** Correo de quien hizo la edicion. */
  createdBy: string;
  createdByName?: string;
  flowVersionBefore: string;
  flowVersionAfter: string;
  stagesAdded: FlowEditHistoryStage[];
  stagesHidden: FlowEditHistoryStage[];
  stagesUpdated: FlowEditHistoryStage[];
  /** Cambio la secuencia de alguna etapa que ya existia. */
  reordered: boolean;
  ordersReset: number;
  ordersReprojected: number;
  eventsDeactivated: number;
}

/** Motivo por el que una edicion cambio el estado de una SO: los mismos del preview. */
export type FlowEditOrderReason = FlowEditResetReason | FlowEditReprojectReason;

export interface FlowEditHistoryOrder {
  serviceOrderId: number;
  reference: string;
  previousStateId: number | null;
  previousStateName: string | null;
  newStateId: number | null;
  newStateName: string | null;
  reason: FlowEditOrderReason;
}

/** Detalle de una edicion, que se pide al desplegarla: GET /api/flow/{flowId}/edit-history/{id}. */
export interface FlowEditHistoryDetail extends FlowEditHistoryEntry {
  /** Orden final de las etapas activas tras la edicion. */
  stagesAfter: (FlowEditHistoryStage & {sequence: number})[];
  /** Solo si el backend puede reconstruirlo: FlowVersionBefore es una huella, no una copia. */
  stagesBefore?: (FlowEditHistoryStage & {sequence: number})[];
  orders: FlowEditHistoryOrder[];
}

export type FlowEditJobStatus = 'Running' | 'Completed' | 'Failed' | 'Conflict';

/**
 * Si el usuario autenticado puede editar un flujo que ya tiene ordenes (SOU-539).
 *
 * Lo decide el servidor con la misma regla que aplica en edit-preview y edit-with-orders,
 * asi la pantalla no guarda su propia copia de que roles pueden hacerlo.
 */
export interface FlowEditPermission {
  allowed: boolean;
  /** Motivo, cuando no se permite. Es el mismo texto que acompañaria al 403. */
  reason?: string;
}

export interface FlowEditJob {
  jobId: string;
  status: FlowEditJobStatus;
  result?: FlowEditResult;
  error?: string;
}


